/**
 * The faces this platform is set in, and which token weight each cut answers.
 *
 * ONE table, and everything that serves a font is DERIVED from it. Every PDF
 * renderer builds its `@font-face` block from these rows and inlines the files
 * as base64; `scripts/fonts/generate-fonts-css.ts` writes the browser's block into
 * `apps/web/src/styles/fonts.css`, which is a generated artifact and not a
 * stylesheet anybody edits. Both emit CSS read by Chromium, so the two are the
 * same declaration rather than two that agree today.
 *
 * That is the point of the shape: a check comparing two authored copies can
 * only report drift after somebody writes it, while a derived one cannot drift
 * at all. What the check still asserts is what derivation cannot — that the
 * files exist, that the generated file on disk is current, and that no step of
 * the weight scale is left uncovered on either surface.
 *
 * Here rather than beside any one artifact: the luach, the aliyos roster and
 * whatever is published next are all set in these, and a shared asset named
 * for one of its consumers tells the next author it belongs to that consumer.
 * They sit beside `tokens.css` because they are the same layer — what the
 * design is made of, before anything decides what to make.
 *
 * ## A cut declares a RANGE, and every token weight lands inside one
 *
 * These families ship discrete cuts; the weight scale has five steps
 * (400 · 450 · 500 · 600 · 700). A cut pinned to one number leaves the other
 * four to CSS weight matching, which resolves them by climbing to the nearest
 * cut it holds — and where nothing at 600 or above exists, the renderer
 * SYNTHESISES the weight by smearing the outline instead.
 *
 * **Synthetic bold is never acceptable here.** It is a thickened approximation
 * of a drawn shape: it wrecks Hebrew letterforms, which carry their meaning in
 * stroke contrast, and on a time it prints a figure nobody drew. So each cut
 * names the span of token weights it answers, and the check asserts the five
 * steps are covered with no gap — a family that cannot cover the scale is a
 * family that needs another cut vendored, not a weight left to the renderer.
 *
 * ## The spans are measured, and measured here as STEM WIDTH
 *
 * Inter is what these sit beside in the interface, so it is what they are
 * matched to. Stem width — the width of one vertical stroke, in units of the em
 * — serves here because both faces are sans and draw every stroke near enough
 * alike, so the one stroke a stem measures stands for the rest.
 *
 * It is not the general answer, and `.agents/contexts/typography.md` carries
 * that: a stem matches only the THICKEST stroke, which is most of the letter
 * where a face has contrast. A face with any is matched on INK instead — and
 * ink is not comparable raw across scripts, so what makes it usable is
 * measuring the pair's own balance rather than assuming one.
 *
 *   token        Inter    cut               apart
 *   400 body     .085     Text     .095     see below
 *   450 label    .095     Text     .095     exact
 *   500 title    .110     Medium   .115     +4.5%
 *   600 strong   .125     SemiBold .130     +4%
 *   700 bold     .145     Bold     .150     +3.4%
 *
 * Size needs no such correction: x-height is .516 in both, and x-height governs
 * how big text looks. Inter's caps run 4% taller, and a `size-adjust` chasing
 * that would break the match that matters to fix the one that does not.
 *
 * The body step is the one a number does not settle. Stem width UNDER-PREDICTS
 * Inter's color — its caps are taller and it sets tighter, so it reads darker
 * than .085 says — and judged at the size it is actually read at, Regular
 * (.080) is visibly the lighter line while Text sits level. On paper the
 * question is a different one, and the surfaces part company there; see
 * `surface` below.
 *
 * Adanim Sans draws its Hebrew and its Latin to the same stem (Medium
 * .115/.115, SemiBold .130/.135), which is what lets one cut serve a bilingual
 * pair without the halves coming out different colors.
 */
/**
 * How a face is written into a `src`, read off the file rather than recorded
 * beside it — a format that can disagree with its own file is a face that
 * silently fails to load.
 *
 * The vendored set is mixed because the cuts are: most ship as TrueType, and
 * the Text cut of each family is published only as woff2. Both are read by
 * Chromium, which is what renders the screen AND the PDF, so the mix costs
 * nothing and buys the one step that would otherwise be missing.
 */
export const fontFormat = (file: string) =>
  file.endsWith('.woff2')
    ? { mime: 'font/woff2', format: 'woff2' }
    : { mime: 'font/ttf', format: 'truetype' }

export type FontFace = {
  family: string
  /**
   * The span of token weights this cut answers, as CSS writes it in a
   * `@font-face` descriptor: `400 500`. A single number is a span of one and is
   * written as one — `400`.
   */
  weight: string
  file: string
  /** The characters this subset answers, in CSS `unicode-range` syntax. */
  unicodeRange?: string
  /**
   * Where this face appears — the surfaces rule, applied to type.
   *
   * `both` is inlined into every PDF as well as loaded by the browser.
   * `interface` is the app's alone: the luach reassigns its families, so a face
   * it never sets would be megabytes of base64 in every sheet for nothing.
   * `print` is the sheet's alone, and exists for one step — see below.
   *
   * The renderers FILTER on this; they never test a family name. One table
   * either way, because the question "what is this platform set in" has one
   * answer, and a second list beside this one is the copy that goes stale.
   *
   * ## The body step is the one place the two surfaces differ, deliberately
   *
   * Everywhere else a token weight resolves to the same cut on both, and the
   * check enforces it — the screen has to predict the paper, or a luach read
   * on a phone stops standing for the one on the wall.
   *
   * At the body step it does not, because the two media are not asking the
   * same question. On screen this text sits beside Inter and has to match it,
   * which Text does and Regular reads light against. On paper there is no Inter
   * to match: the sheet is entirely Adanim and Alef, it is read across a room,
   * and ink spreads. Regular is the sheet's own color.
   *
   * So the divergence is stated here, in the table, as two rows — not left to
   * a stylesheet override that the next reader would meet as a surprise.
   */
  surface: 'both' | 'interface' | 'print'
}

/**
 * What each family is called, named here because this is where a face is named
 * — the way `brand.ts` is where a product is named.
 *
 * A family name cannot be indirected everywhere it is needed: it is baked into
 * the binary's own name table, and the stylesheets carrying it are read off
 * disk as text by the PDF renderers and never compiled. So it is written once
 * HERE, and every piece of TypeScript that needs it reaches for these rather
 * than spelling it again.
 */
export const FAMILY = {
  /** The text: Hebrew, the Latin beside it, and a book's running text. */
  sans: 'Adanim Sans',
  /** Page titles, Latin only. */
  serif: 'Adanim Serif',
  /** Figures. */
  mono: 'IBM Plex Mono',
  /** The luach's Hebrew display type. */
  display: 'Alef',
  /** A studio book's Hebrew display type — a standalone Hebrew line. */
  book: 'Alephbeis Vilna',
  /** Names written onto a printed sheet by hand. */
  hand: 'Playpen Sans Hebrew',
  /**
   * Musical notation, and the only face here that sets no language.
   *
   * It has a `--family-` token like the others because a stylesheet may not
   * write a face's name — but no `--font-` role, because it answers no
   * typographic question. Nothing falls back to it and it falls back to
   * nothing: a stave set in anything else is not a stave.
   */
  music: 'Bravura',
} as const

/**
 * The families as CSS custom properties, so no stylesheet spells one.
 *
 * A face's NAME is as much a fact of the table as which cut answers which
 * weight, and it was the one fact every stylesheet repeated by hand — eighteen
 * spellings across five files, which made renaming a face a find-and-replace
 * rather than an edit. Emitted beside the `@font-face` block by both renderers
 * and by the browser's generated sheet, it is declared before anything reads
 * it, and a rename is this table and nothing else.
 */
export const familyTokens = (): string =>
  `:root {\n${Object.entries(FAMILY)
    .map(([name, family]) => `  --family-${name}: "${family}";`)
    .join('\n')}\n}\n`

export const FONT_FACES: FontFace[] = [
  /*
   * Inter — the interface face, and the only variable one: it draws every step
   * of the scale from a single file, which is what the other families are
   * matched against. Interface-only; the luach never sets it.
   */
  {
    family: 'Inter Variable',
    weight: '100 900',
    file: 'Inter-Variable.woff2',
    surface: 'interface',
  },
  /*
   * Bravura — musical notation, and the only face here that sets no language.
   *
   * The reference font for SMuFL, which is why it is this one: its codepoints
   * are the standard, so another notation face can replace it as a font change
   * with nothing in the code touched. A stave drawn from whatever glyphs a
   * reader's machine happens to carry is not notation — the clef rendered and
   * the rests came out as empty boxes, which is the whole argument.
   *
   * **Vendored unmodified, so it keeps its own name.** Its license reserves
   * "Bravura", and OFL forbids a CHANGED copy carrying a reserved name — the
   * reason this repo has an Adanim Sans rather than an IBM Plex Sans Hebrew.
   * Untouched, the clause does not bite and the name stays. Which also rules
   * out subsetting it to save bytes: a subset is a modification, and the few
   * hundred kilobytes are not worth taking on the rename.
   *
   * One span rather than cuts, because a symbol font has no weights to choose
   * between — a notehead is not bolder in a heading.
   */
  {
    family: 'Bravura',
    weight: '400 700',
    file: 'Bravura.woff2',
    surface: 'both',
  },
  /*
   * Playpen Sans Hebrew — names written onto a printed sheet. The two files
   * are Google Fonts' unmodified Hebrew and Latin subsets of one variable
   * family; their ranges keep Chromium on the right drawing for each script.
   * A single variable axis covers every weight the family ships (100–800),
   * including the 350 used for a name that reads as written in.
   */
  {
    family: 'Playpen Sans Hebrew',
    weight: '100 800',
    file: 'PlaypenSansHebrew-Hebrew.woff2',
    unicodeRange:
      'U+0307-0308, U+0590-05FF, U+200C-2010, U+20AA, U+25CC, U+FB1D-FB4F',
    surface: 'both',
  },
  {
    family: 'Playpen Sans Hebrew',
    weight: '100 800',
    file: 'PlaypenSansHebrew-Latin.woff2',
    unicodeRange:
      'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD',
    surface: 'both',
  },
  /*
   * Alef — the luach's Hebrew display face. Two cuts, so the scale divides
   * where the family does: everything below the strong step is Regular, and
   * strong and bold are Bold.
   */
  {
    family: 'Alef',
    weight: '400 500',
    file: 'Alef-Regular.ttf',
    surface: 'both',
  },
  { family: 'Alef', weight: '600 700', file: 'Alef-Bold.ttf', surface: 'both' },
  /*
   * Alephbeis Vilna — a studio book's Hebrew display face: the verses and the
   * source quotes, which are the most prominent Hebrew on a printed page and
   * the face a sefer is read in. Two cuts, so the scale divides where the
   * family does.
   *
   * A STANDALONE Hebrew line only, and keeping it to that one job is what makes
   * it read as the book's other voice. Hebrew woven into an English sentence
   * takes the text face like the words around it.
   */
  {
    family: 'Alephbeis Vilna',
    weight: '400 500',
    file: 'AlephbeisVilna-Regular.woff2',
    surface: 'both',
  },
  {
    family: 'Alephbeis Vilna',
    weight: '600 700',
    file: 'AlephbeisVilna-Bold.woff2',
    surface: 'both',
  },
  /*
   * Alephbeis Dyslexia — a Hebrew face drawn to be told apart letter by letter,
   * for a reader who finds the ordinary ones run together. It is here to be
   * LOOKED at while it is worked on: the type page sets every family in the
   * table, so a face in the table is a face whose changes can be seen in place
   * rather than guessed at from a glyph on a stage.
   *
   * It has no semantic token and so no job on any screen. That is the point for
   * now — a family earns a role by being chosen for one, and this one is being
   * repaired. What it is FOR, when it has a job, is a reading option somebody
   * turns on for themselves.
   */
  {
    family: 'Alephbeis Dyslexia',
    weight: '400 500',
    file: 'AlephbeisDyslexia-Regular.woff2',
    surface: 'both',
  },
  /*
   * Alephbeis Kesiv — print letters for teaching: worksheets and early
   * readers. It is here to be looked at while the teaching artifacts are
   * built, with no semantic token until one of them actually sets it.
   *
   * INTERFACE only for now. PDF renderers inline every face in their surface,
   * and a family with no printed consumer does not yet belong in every luach.
   * Trace and Trace Start are separate CSS families so the specimen can show
   * each drawing instead of choosing one of three Regular faces arbitrarily.
   */
  {
    family: 'Alephbeis Kesiv',
    weight: '400 450',
    file: 'AlephbeisKesiv-Light.woff2',
    surface: 'interface',
  },
  {
    family: 'Alephbeis Kesiv',
    weight: '500 600',
    file: 'AlephbeisKesiv-Regular.woff2',
    surface: 'interface',
  },
  {
    family: 'Alephbeis Kesiv',
    weight: '700',
    file: 'AlephbeisKesiv-Bold.woff2',
    surface: 'interface',
  },
  {
    family: 'Alephbeis Kesiv Trace',
    weight: '400 700',
    file: 'AlephbeisKesivTrace-Regular.woff2',
    surface: 'interface',
  },
  {
    family: 'Alephbeis Kesiv Trace Start',
    weight: '400 700',
    file: 'AlephbeisKesivTraceStart-Regular.woff2',
    surface: 'interface',
  },
  {
    family: 'Alephbeis Dyslexia',
    weight: '600 700',
    file: 'AlephbeisDyslexia-Bold.woff2',
    surface: 'both',
  },
  /*
   * Alephbeis Sans — the Hebrew we commissioned, now setting English too.
   *
   * Here for the same reason Dyslexia is: to be LOOKED at while it is worked
   * on. It has no semantic token and no job on any screen, and it earns one by
   * being chosen for one rather than by being present.
   *
   * INTERFACE only, and that is about cost rather than intent. The PDF
   * renderers inline every face they are given as base64, so ten cuts of a
   * family nothing prints would put a fifth of a megabyte into every luach.
   * When it has a printed job it takes `both` and pays for it there.
   *
   * Ten cuts of Hebrew, EIGHT of which also set English. The lightest two are
   * Hebrew alone: the Latin does not reach them — matching the Hairline would
   * need one nearly three times too heavy — and a companion that wrong reads as
   * the face's opinion rather than as a gap, where a fallback at least looks
   * like one. `scripts/fonts/font_pair.py` and `scripts/fonts/font_join.py` build these,
   * and the reasoning lives in those files rather than here.
   */
  {
    family: 'Alephbeis Sans',
    weight: '50',
    file: 'AlephbeisSans-Hairline.woff2',
    surface: 'interface',
  },
  {
    family: 'Alephbeis Sans',
    weight: '100',
    file: 'AlephbeisSans-Thin.woff2',
    surface: 'interface',
  },
  {
    family: 'Alephbeis Sans',
    weight: '200',
    file: 'AlephbeisSans-ExtraLight.woff2',
    surface: 'interface',
  },
  {
    family: 'Alephbeis Sans',
    weight: '300',
    file: 'AlephbeisSans-Light.woff2',
    surface: 'interface',
  },
  {
    family: 'Alephbeis Sans',
    weight: '400 450',
    file: 'AlephbeisSans-Regular.woff2',
    surface: 'interface',
  },
  {
    family: 'Alephbeis Sans',
    weight: '500',
    file: 'AlephbeisSans-Medium.woff2',
    surface: 'interface',
  },
  {
    family: 'Alephbeis Sans',
    weight: '600',
    file: 'AlephbeisSans-SemiBold.woff2',
    surface: 'interface',
  },
  {
    family: 'Alephbeis Sans',
    weight: '700',
    file: 'AlephbeisSans-Bold.woff2',
    surface: 'interface',
  },
  {
    family: 'Alephbeis Sans',
    weight: '800',
    file: 'AlephbeisSans-ExtraBold.woff2',
    surface: 'interface',
  },
  {
    family: 'Alephbeis Sans',
    weight: '900',
    file: 'AlephbeisSans-Black.woff2',
    surface: 'interface',
  },
  /*
   * Adanim Sans — the working text: labels, glosses, controls, the Latin of a
   * bilingual pair, and the whole of a book's running text. Its Hebrew and its
   * Latin are drawn to the same stem, so one cut serves both scripts and a
   * bilingual pair cannot come out two colors — which is what lets a book that
   * is bilingual to the sentence be set in one family.
   */
  /* The body step, and the one place the surfaces part company. Text beside
     Inter on screen; Regular on paper, which has no Inter to match. */
  {
    family: 'Adanim Sans',
    weight: '400 450',
    file: 'AdanimSans-Text.woff2',
    surface: 'interface',
  },
  {
    family: 'Adanim Sans',
    weight: '400',
    file: 'AdanimSans-Regular.ttf',
    surface: 'print',
  },
  {
    family: 'Adanim Sans',
    weight: '450',
    file: 'AdanimSans-Text.woff2',
    surface: 'print',
  },
  {
    family: 'Adanim Sans',
    weight: '500',
    file: 'AdanimSans-Medium.ttf',
    surface: 'both',
  },
  {
    family: 'Adanim Sans',
    weight: '600',
    file: 'AdanimSans-SemiBold.ttf',
    surface: 'both',
  },
  {
    family: 'Adanim Sans',
    weight: '700',
    file: 'AdanimSans-Bold.ttf',
    surface: 'both',
  },
  /*
   * IBM Plex Mono — the figures, and the reason the no-synthesis rule is not a
   * matter of taste: a time is the product, and a synthesised one is a figure
   * nobody drew. Four cuts, so the scale is a scale here too.
   */
  /* The figures follow the Hebrew they sit beside, on both surfaces: a year
     table whose times were a step heavier than its parsha names would be the
     mismatch this ladder exists to prevent. */
  {
    family: 'IBM Plex Mono',
    weight: '400 450',
    file: 'IBMPlexMono-Text.woff2',
    surface: 'interface',
  },
  {
    family: 'IBM Plex Mono',
    weight: '400',
    file: 'IBMPlexMono-Regular.ttf',
    surface: 'print',
  },
  {
    family: 'IBM Plex Mono',
    weight: '450',
    file: 'IBMPlexMono-Text.woff2',
    surface: 'print',
  },
  {
    family: 'IBM Plex Mono',
    weight: '500',
    file: 'IBMPlexMono-Medium.ttf',
    surface: 'both',
  },
  {
    family: 'IBM Plex Mono',
    weight: '600',
    file: 'IBMPlexMono-SemiBold.ttf',
    surface: 'both',
  },
  {
    family: 'IBM Plex Mono',
    weight: '700',
    file: 'IBMPlexMono-Bold.ttf',
    surface: 'both',
  },
  /*
   * Adanim Serif — the page-title voice, in Latin and Hebrew both, and on paper
   * as well as on screen: a studio book sets its title in it, alone on a page
   * with nothing beside it to clash with. The luach does not — it titles in
   * Alef and works in Adanim Sans — so the sheet inlines four cuts it never
   * sets. That is what one axis costs. `surface` says which MEDIUM a cut
   * belongs to, and paper now holds two artifacts that are set differently;
   * the alternative is a per-artifact list, which is a second table for every
   * renderer to keep in step.
   */
  {
    family: 'Adanim Serif',
    weight: '400 450',
    file: 'AdanimSerif-Text.woff2',
    surface: 'both',
  },
  {
    family: 'Adanim Serif',
    weight: '500',
    file: 'AdanimSerif-Medium.ttf',
    surface: 'both',
  },
  {
    family: 'Adanim Serif',
    weight: '600',
    file: 'AdanimSerif-SemiBold.ttf',
    surface: 'both',
  },
  {
    family: 'Adanim Serif',
    weight: '700',
    file: 'AdanimSerif-Bold.ttf',
    surface: 'both',
  },
]
